/**
 * Pure helpers of the Wiring step's "Circuits and interconnection" card (permits/WiringSchedule.tsx): the choices are
 * the permit site details' `electrical` object (api PermitElectricalInputs), saved with the site details; the
 * schedule rows come from the preview's `electrical.wiring` (app/design/permits/wiring.py). Tested in
 * permitWiring.test.ts.
 */
import type { MessageKey } from "../i18n/keys";
import type { Tr } from "./permits.ts";

export type ConductorRole = "pv_array" | "pv_home_run" | "ac_trunk" | "ac_branch_home_run" | "inverter_output" | "combiner_output" | "service";

export interface RunInput {
  size?: string | null;
  mm2?: number | null;
  length_m?: number | null;
}

export interface ElectricalInputs {
  nec_edition?: 2017 | 2020 | 2023 | null;
  interconnection?: "load_side" | "load_side_derate" | "supply_side" | null;
  main_breaker_derate_a?: number | null;
  meter_main?: "separate" | "combination" | null;
  ac_disconnect?: "auto" | "non_fusible" | "fusible" | null;
  conduit?: "emt" | "pvc40" | "pvc80" | null;
  rooftop_low?: boolean | null;
  voltage_drop_target_pct?: number | null;
  three_line?: boolean | null;
  tags?: Partial<Record<ConductorRole, RunInput>>;
}

export const AWG_SIZES = ["12 AWG", "10 AWG", "8 AWG", "6 AWG", "4 AWG", "3 AWG", "2 AWG", "1 AWG", "1/0 AWG", "2/0 AWG", "3/0 AWG", "4/0 AWG"] as const;
export const MM2_SIZES = [1.5, 2.5, 4, 6, 10, 16, 25, 35, 50] as const;

/** Option labels are catalog keys (`t(o.label)` where rendered). */
export const INTERCONNECTION_OPTIONS: readonly { value: "load_side" | "load_side_derate" | "supply_side"; label: MessageKey }[] = [
  { value: "load_side", label: "permit.wiring.interconnection.loadSide" },
  { value: "load_side_derate", label: "permit.wiring.interconnection.loadSideDerate" },
  { value: "supply_side", label: "permit.wiring.interconnection.supplySide" },
];

export const DISCONNECT_OPTIONS: readonly { value: "auto" | "non_fusible" | "fusible"; label: MessageKey }[] = [
  { value: "auto", label: "permit.wiring.automatic" },
  { value: "non_fusible", label: "permit.wiring.disconnect.nonFusible" },
  { value: "fusible", label: "permit.wiring.disconnect.fusible" },
];

export const CONDUIT_OPTIONS: readonly { value: "emt" | "pvc40" | "pvc80"; label: MessageKey }[] = [
  { value: "emt", label: "permit.wiring.conduit.emt" },
  { value: "pvc40", label: "permit.wiring.conduit.pvc40" },
  { value: "pvc80", label: "permit.wiring.conduit.pvc80" },
];

export const EDITION_OPTIONS: readonly { value: "auto" | "2017" | "2020" | "2023"; label: MessageKey }[] = [
  { value: "auto", label: "permit.wiring.automatic" },
  { value: "2017", label: "permit.wiring.edition.nec2017" },
  { value: "2020", label: "permit.wiring.edition.nec2020" },
  { value: "2023", label: "permit.wiring.edition.nec2023" },
];

/** Drops empty values so the saved object stays minimal (an empty choice means the default). */
function clean<T extends object>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v == null || v === "" || (typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0)) continue;
    out[k] = v;
  }
  return out as T;
}

/** The details with `patch` merged into their `electrical` choices. */
export function withElectrical<D extends { electrical?: ElectricalInputs | null }>(details: D, patch: Partial<ElectricalInputs>): D {
  const next = clean({ ...(details.electrical ?? {}), ...patch });
  return { ...details, electrical: Object.keys(next).length ? next : null };
}

/** The details with one run's size or length changed (null removes it: computed again). */
export function withRun<D extends { electrical?: ElectricalInputs | null }>(details: D, role: ConductorRole, patch: RunInput): D {
  const tags = { ...(details.electrical?.tags ?? {}) };
  const run = clean({ ...(tags[role] ?? {}), ...patch });
  if (Object.keys(run).length) tags[role] = run;
  else delete tags[role];
  return withElectrical(details, { tags: Object.keys(tags).length ? tags : undefined } as Partial<ElectricalInputs>);
}

const LENGTH_SOURCE: Record<string, MessageKey> = {
  entered: "permit.wiring.lengthSource.entered",
  "site plan path + typical drop": "permit.wiring.lengthSource.sitePlan",
  "array layout": "permit.wiring.lengthSource.arrayLayout",
  typical: "permit.wiring.lengthSource.typical",
};

/** A run's length as shown: feet or metres, with where it came from (an unknown source is the server's words). */
export function lengthLabel(t: Tr, metres: number, source: string, units: "imperial" | "metric"): string {
  const length = units === "imperial" ? t("permit.wiring.lengthFt", { value: Math.round(metres / 0.3048) }) : t("permit.wiring.lengthM", { value: metres });
  const key = LENGTH_SOURCE[source];
  return t("permit.wiring.lengthWithSource", { length, source: key ? t(key) : source });
}

/** Feet or metres typed → metres (null when empty). */
export function lengthToMetres(value: number | null, units: "imperial" | "metric"): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return units === "imperial" ? Math.round(value * 0.3048 * 100) / 100 : value;
}

/** How many choices differ from the defaults (the card's badge). */
export function changedCount(e: ElectricalInputs | null | undefined): number {
  if (!e) return 0;
  const { tags, ...rest } = e;
  return Object.values(rest).filter((v) => v != null).length + Object.keys(tags ?? {}).length;
}
